import { z } from 'zod';
import { AttachmentSchema } from './attachment.js';
import { ChannelKindSchema } from './channel-kind.js';
import { SenderIdentitySchema } from './sender.js';
/**
 * Outbound message, as produced by the core pipeline before dispatch. The
 * channel adapter turns this into an actual provider send.
 *
 * No `nativeMessageId` on `channel` yet — that's assigned by the provider and
 * flows back via `SendReceipt.nativeMessageId`.
 */
export const NormalizedOutboundMessageSchema = z.object({
    schemaVersion: z.literal(1),
    messageId: z.guid(),
    conversationId: z.guid(),
    channel: z.object({
        type: ChannelKindSchema,
        accountId: z.string().min(1),
    }),
    recipient: SenderIdentitySchema,
    threading: z.object({
        inReplyToExternalId: z.string().nullable(),
        externalThreadRefs: z.array(z.string()).default([]),
        nativeThreadId: z.string().nullable(),
        /**
         * RFC 5322 `Message-ID` the core MINTED for this outbound BEFORE dispatch
         * (full `<uuid@domain>` form). The email channel adapter stamps it onto the
         * MIME `Message-ID` header (`sendViaGmail` → `mailOpts.messageId`) so the
         * value the recipient sees — and cites as `In-Reply-To` in their reply — is
         * known to us up front. `MessageStorePg.insertOutbound` persists the
         * NORMALIZED form into `message.rfc_message_id` immediately, so a send that
         * crashes AFTER dispatch but BEFORE `updateDeliveryStatus` still leaves the
         * row carrying its rfc id: the Gmail poller's `ingestOutbound` then collapses
         * the SENT copy onto it via the RFC dedup branch (no permanent duplicate /
         * "pending"). Optional — channels that can't pre-mint a Message-ID omit it,
         * and the post-send `messages.get` recovery in `updateDeliveryStatus`
         * remains the source of truth if the provider rewrites the header.
         */
        rfcMessageId: z.string().optional(),
    }),
    content: z.object({
        subject: z.string().nullable(),
        text: z.string().nullable(),
        html: z.string().nullable(),
        attachments: z.array(AttachmentSchema).default([]),
    }),
    metadata: z.object({
        enqueuedAt: z.string().datetime({ offset: true }),
        dispatchedAt: z.string().datetime({ offset: true }).nullable(),
    }),
});
//# sourceMappingURL=normalized-outbound-message.js.map